import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { enumCompanyBusinessType, enumCompanyPartnershipType } from '@data-layer/enums/companies';
import { BUSINESS_TYPES, isPartnership, isStateOfIncorporationApplicable, PARTNERSHIP_TYPES } from '@constants/business-types';
import { requiresSs4Document } from '@constants/company-documents';
import { USA_COUNTRY_CODE } from '@constants/constants';
import { CompaniesService } from '@services/companies.service';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { enumCbpAssignedNumberStatus, enumIdentifierQualifier } from '@data-layer/enums/common';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { WidgetsService } from '@services/widgets.service';
import { DependencyModel } from '@data-layer/models/dependencies';
import { DocumentModel } from '@data-layer/models/documents';
import { enumUserBetaFlag } from '@app/data-layer/enums/user';
import { UserBetaFlagModel } from '@app/data-layer/models/user';
import { enumDependencyState } from '@app/data-layer/enums/dependencies';
import { CompanyModel } from '@app/data-layer/models/companies';
import { CUSTOM_MASK } from '@helpers/custom-validator';

@Component({
  selector: 'company-business-info-form',
  templateUrl: './company-business-info-form.component.html',
  styleUrls: ['./company-business-info-form.component.scss'],
})
export class CompanyBusinessInfoFormComponent implements OnInit, OnDestroy {
  @Input() public company: CompanyModel;
  @Input() public formGroup: UntypedFormGroup;
  @Input() public isEditView: boolean;
  @Input() public isFirstCompany: boolean;
  @Input() public isShowBusinessLocation: boolean = true;
  @Input() public isShowDescription: boolean = false;
  @Input() public isRegistrationInProcess: boolean = false;
  @Input() public lastDependencyErrorCodes: string[];
  @Input() public lastDependency: DependencyModel;
  // #13356 — SS-4 block state is owned by edit-company (it owns the company documents and the upload buffer)
  @Input() public ss4Document: DocumentModel | null = null;
  @Input() public ss4DownloadUrl: string | null = null;
  @Input() public isLoadingSs4: boolean = false;
  @Input() public isSs4Image: boolean = false;
  /** #13356 — POA already signed/paid: the SS-4 is shown as recommended and does not gate Sign. */
  @Input() public isPoaGrandfathered: boolean = false;

  @Output() public applyForAssignedCbpNumber: EventEmitter<void> = new EventEmitter<void>();
  @Output() public ss4Selected$: EventEmitter<File> = new EventEmitter<File>();

  public CUSTOM_MASK: any = CUSTOM_MASK;
  public identifierErrors: IErrorForm = {
    required: 'FORM_ERROR.REQUIRED',
    backError: '',
    notMatchEIN: 'FORM_ERROR.NOT_MATCH_EIN',
    wrongFormatIEN: 'FORM_ERROR.WRONG_FORMAT_EIN',
    notMatchSSN: 'FORM_ERROR.NOT_MATCH_SSN',
  };
  // #13269 — the GP/LP/LLP toggle can only ever be required-invalid
  public partnershipTypeErrors: IErrorForm = {
    required: 'FORM_ERROR.REQUIRED',
    backError: '',
  };
  public isAllowedRegistrationInitialState: boolean = false;
  public enumIdentifierQualifier: typeof enumIdentifierQualifier = enumIdentifierQualifier;
  public businessTypes: Array<{ value: enumCompanyBusinessType; description: string }> = BUSINESS_TYPES;
  // #13269 — GP / LP / LLP toggle options for the Partnership business type
  public partnershipTypes: Array<{ value: enumCompanyPartnershipType; description: string }> = PARTNERSHIP_TYPES;
  // #13188 — fixed US country for the State of Incorporation states dropdown (US-only field)
  public usCountryFc: UntypedFormControl = new UntypedFormControl({ code: USA_COUNTRY_CODE });
  private destroy$: ReplaySubject<boolean> = new ReplaySubject();

  constructor(private cdr: ChangeDetectorRef, private widgetsService: WidgetsService, private companiesService: CompaniesService) {}

  public ngOnInit(): void {
    this.isAllowedRegistrationInitialState = this.formGroup.get('isAllowedRegistration').value;
    this.companiesService.companyUpdated$.pipe(takeUntil(this.destroy$)).subscribe(() => this.cdr.markForCheck());

    if (!this.isRegistrationEnable()) {
      this.formGroup.get('isAllowedRegistration').disable();
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public isRegistrationEnable(): boolean {
    return (
      (!this.formGroup.get('isAllowedRegistration')?.value && !this.lastDependency?.state) ||
      !this.companiesService.getCompanyById(this.formGroup.get('id').value)?.validationDate ||
      this.formGroup.get('businessName').dirty ||
      this.formGroup.get('identifier').dirty ||
      this.formGroup.get('identifierQualifier').dirty ||
      this.formGroup.get('businessType').dirty ||
      this.formGroup.get('address').dirty
    );
  }

  public contactSupport(event: Event): void {
    event.preventDefault();
    this.widgetsService.openWidget(['Registration failed']);
  }

  public onAssignedCbpActionClick() {
    this.applyForAssignedCbpNumber.emit();
  }

  public get isDependencyStateAccepted(): boolean {
    return this.lastDependency?.state === enumDependencyState.ACCEPTED;
  }

  public get isUsa(): boolean {
    return this.formGroup.controls.isUnitedStates.value;
  }

  // #13188 — State of Incorporation shown only for US Corporation/LLC/Partnership.
  // #13520 — and only while the ADDRESS country is the US (a foreign ANI importer hides it).
  // The control-exists guard protects placements whose form definitions omit the field
  // (ES/ISF/LCL/bonds sidebars) — state-dropdown throws on a missing stateFc (#13233).
  public get isStateOfIncorporationVisible(): boolean {
    if (!this.formGroup.get('stateOfIncorporation')) {
      return false;
    }
    const countryControl: AbstractControl | null = this.formGroup.get(['address', 'country']);
    return isStateOfIncorporationApplicable(
      this.isUsa,
      countryControl ? countryControl.value : null,
      this.formGroup.get('businessType').value,
    );
  }

  // #13269 — GP/LP/LLP selector shown only for the Partnership business type.
  // The control-exists guard protects placements whose form definitions omit the field (#13233).
  public get isPartnershipTypeVisible(): boolean {
    return !!this.formGroup.get('partnershipType') && isPartnership(this.formGroup.get('businessType').value);
  }

  // #13356 — the SS-4 block is rendered only for EIN-registered companies. The control-exists guard
  // protects placements whose form definitions omit the field (#13233).
  public get isSs4Visible(): boolean {
    const qualifierControl: AbstractControl | null = this.formGroup.get('identifierQualifier');
    return this.isUsa && !!qualifierControl && requiresSs4Document(qualifierControl.value);
  }

  // #13356 — required until the POA is signed/paid; after that the document is only recommended
  public get isSs4Required(): boolean {
    return !this.isPoaGrandfathered;
  }

  public get isSs4NonImageDocument(): boolean {
    return !!this.ss4Document && !this.isSs4Image;
  }

  public get ss4FileName(): string {
    return this.ss4Document ? this.ss4Document.fileName : '';
  }

  // #13356 — 0/null on the create flow: the uploader then buffers the file instead of showing a loader
  public get companyId(): number | null {
    const idControl: AbstractControl | null = this.formGroup.get('id');
    return idControl ? idControl.value : null;
  }

  public setPartnershipType(value: enumCompanyPartnershipType): void {
    const control = this.formGroup.get('partnershipType');
    control.patchValue(value);
    control.markAsDirty();
    control.markAsTouched();
  }

  public get businessInfoDescription(): string {
    const longDescription = this.isHasAssignedCbpRequest ? 'LABELS.BUSINESS_CBP_INFO_DESCRIPTION' : 'LABELS.BUSINESS_INFO_DESCRIPTION';
    return this.isUsa ? longDescription : 'LABELS.BUSINESS_INFO_DESCRIPTION_SHORT';
  }

  public get isAssignedCbpRequestSubmittedOrApproved(): boolean {
    return this.isHasAssignedCbpRequest && this.isEditView && (this.isAssignedCbpRequestSubmitted || this.isAssignedCbpRequestApproved);
  }

  public get isAssignedCbpRequestSubmitted(): boolean {
    return (
      this.company?.cbpAssignedNumberRegistration?.status === enumCbpAssignedNumberStatus.SENT ||
      this.company?.cbpAssignedNumberRegistration?.status === enumCbpAssignedNumberStatus.SENDING
    );
  }

  public get isAssignedCbpRequestApproved(): boolean {
    return this.company?.cbpAssignedNumberRegistration?.status === enumCbpAssignedNumberStatus.COMPLETED;
  }

  public get isAssignedCbpRequestRejected(): boolean {
    return this.company?.cbpAssignedNumberRegistration?.status === enumCbpAssignedNumberStatus.REJECTED;
  }

  public get isIdentifierCompanyUpdateDisabled(): boolean {
    return (
      this.formGroup.get('isLockedIdentifier').value || this.isAssignedCbpRequestSubmittedOrApproved || this.isAssignedCbpRequestRejected
    );
  }

  public get isOtherIdentifierQualifierSelected(): boolean {
    return this.formGroup.get('identifierQualifier').value === enumIdentifierQualifier.ANI;
  }

  public get isHasAssignedCbpRequest(): boolean {
    return this.companiesService
      .getUser()
      ?.betaFlags?.map((flag: UserBetaFlagModel) => flag.tag)
      .includes(enumUserBetaFlag.assignedCbpRequest);
  }
}
